/**
 * Finding the controls a bundle has not already bound.
 *
 * `/user/ci/` and `/user/liju/` replace regions of themselves with htmx, and
 * the controls inside those regions are bound by this site's own scripts rather
 * than by htmx. `src/site/htmx-rebinding.ts` calls each binder again over what
 * arrived, so both have to be safe to call twice: a control bound twice posts
 * twice, and a reader pressing Remove would take a word out and put it back.
 *
 * **A set held in the module rather than a marker on the element.** The
 * bookkeeping is this script's own, and `data-cb-*` is markup a reader's
 * stylesheet reads. A control htmx has replaced is unreachable from the
 * document, so the entry goes with it and nothing has to be cleaned up.
 *
 * Shared by `save-word.ts` and `save-liju.ts`, which is what keeps either of
 * them from growing a copy of the same three lines. Both look for a `<div>`
 * wrapping a form, so the elements are `HTMLElement` rather than a parameter
 * neither caller varies.
 */

/**
 * A finder that answers with the matching elements it has not answered with
 * before.
 *
 * One set per call, so the two binders cannot see each other's controls. A save
 * control and a sentence control are different elements anyway, and sharing one
 * set would make that an accident rather than an arrangement.
 * @returns The finder, which takes where to look and what to look for.
 */
export function boundOnce(): (
  within: ParentNode,
  selector: string,
) => readonly HTMLElement[] {
  const bound = new WeakSet<HTMLElement>();

  return (within, selector) => {
    const found = [...within.querySelectorAll<HTMLElement>(selector)].filter(
      (one) => !bound.has(one),
    );

    for (const one of found) {
      bound.add(one);
    }

    return found;
  };
}
